import { markupFormatter } from "../common/markup/markupFormatter";
import { BlockMarkup, BlockMarkupItemType, PlainTextMarkup } from "../common/markup/markup.interface";
import { SubCard, TtsVoice } from "../common/api";
import { getNextSubcardIndex } from "../review/utils/multicard.utils";
import { expandAbbreviations } from "./abbreviations";

/** A language the speech recognizer listens in. */
export type SttLanguage = 'ru' | 'el' | 'en';

/** Subcards voiced for a card whose subcards have no `@Group`. */
export const UNGROUPED_SUBCARD_COUNT = 3;

const MAX_SPEECH_LENGTH = 1000;
const GREEK_LETTER = /[Ͱ-Ͽἀ-῿]/;
const GAP = /_{2,}/;
const BOLD = /\*\*(.+?)\*\*/g;
const CYRILLIC_LETTER = /[а-яё]/i;

export type CarSegment =
    | { kind: 'speech'; text: string; voice: TtsVoice }
    | { kind: 'gap' };

/** One question/answer exchange in the car: a subcard, or a whole card without subcards. */
export interface CarItem {
    group?: string;
    question: CarSegment[];
    /** The marked form(s) first, then the full answer. */
    answer: CarSegment[];
    /** Just the full answer, said back after a right answer. */
    sentence: CarSegment[];
    /** What counts as a right spoken answer: the **bold** form(s), else the answer's alternatives. */
    accepted: string[];
    /** The language the answer is spoken in. */
    answerLanguage: SttLanguage;
    /** The subcard behind this item; undefined for a card without subcards. */
    subcard?: SubCard;
}

export interface CarCardPlan {
    hasSubcards: boolean;
    items: CarItem[];
}

export function voiceOf(text: string): TtsVoice {
    return GREEK_LETTER.test(text) ? 'greek' : 'native';
}

export function languageOf(text: string): SttLanguage {
    if (GREEK_LETTER.test(text))
        return 'el';
    return CYRILLIC_LETTER.test(text) ? 'ru' : 'en';
}

export function speech(text: string): CarSegment {
    return { kind: 'speech', text, voice: voiceOf(text) };
}

function cleanText(text: string): string {
    return expandAbbreviations(text)
        .replace(/\*\*/g, '')
        .replace(/\{[^}]*\}/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

/** Turns one line into speech, with the `___` gap as a beep between its parts. */
function lineSegments(line: string): CarSegment[] {
    const parts = line.split(GAP).map(cleanText);
    const segments: CarSegment[] = [];
    parts.forEach((part, index) => {
        if (index > 0)
            segments.push({ kind: 'gap' });
        if (part.length > 0)
            segments.push(speech(part.slice(0, MAX_SPEECH_LENGTH)));
    });
    return segments;
}

function textSegments(text: string): CarSegment[] {
    return text
        .split(/\r?\n/)
        .flatMap(lineSegments);
}

function answerSegments(answer: string): CarSegment[] {
    const full = textSegments(answer);
    const forms = Array.from(answer.matchAll(BOLD), match => cleanText(match[1]))
        .filter(form => form.length > 0);
    const spokenAnswer = full
        .map(segment => (segment.kind === 'speech' ? segment.text : ''))
        .join(' ')
        .trim();
    const formText = forms.join(', ');

    if (formText.length === 0 || formText === spokenAnswer)
        return full;

    return [speech(formText), ...full];
}

/** Markup and parenthesised remarks off: "ломать (что-л.)" is answered with "ломать". */
function answerText(text: string): string {
    return text
        .replace(/\*\*/g, '')
        .replace(/\{[^}]*\}/g, ' ')
        .replace(/\([^)]*\)/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

function acceptedAnswers(answer: string): string[] {
    const forms = Array.from(answer.matchAll(BOLD), match => answerText(match[1]))
        .filter(form => form.length > 0);
    if (forms.length > 0)
        return [forms.join(' ')];

    const firstLine = answer.split(/\r?\n/).map(answerText).find(line => line.length > 0) ?? '';
    return firstLine.split(/[,;/]/).map(part => part.trim()).filter(part => part.length > 0);
}

function voiceItem(question: CarSegment[], answer: string, extra: Partial<CarItem>): CarItem | undefined {
    const answerSegmentsList = answerSegments(answer);
    const accepted = acceptedAnswers(answer);
    if (!hasSpeech(question) || !hasSpeech(answerSegmentsList) || accepted.length === 0)
        return undefined;

    return {
        ...extra,
        question,
        answer: answerSegmentsList,
        sentence: textSegments(answer),
        accepted,
        answerLanguage: languageOf(accepted.join(' ')),
    };
}

function hasSpeech(segments: CarSegment[]): boolean {
    return segments.some(segment => segment.kind === 'speech');
}

function subcardItem(subcard: SubCard): CarItem | undefined {
    return voiceItem(
        textSegments(subcard.question),
        subcard.answer,
        subcard.group !== undefined ? { group: subcard.group, subcard } : { subcard },
    );
}

/**
 * The subcards for one car pass: the first round-robin cycle — one subcard from
 * every `@Group` — or {@link UNGROUPED_SUBCARD_COUNT} random ones when there are no groups.
 */
export function selectCarSubcards(subCards: SubCard[]): SubCard[] {
    const hasGroups = subCards.some(subcard => subcard.group !== undefined && subcard.group.length > 0);
    const count = hasGroups
        ? new Set(subCards.map(subcard => subcard.group ?? '')).size
        : Math.min(UNGROUPED_SUBCARD_COUNT, subCards.length);

    const order: number[] = [];
    while (order.length < count) {
        const next = getNextSubcardIndex(subCards, order);
        if (next === undefined)
            break;
        order.push(next);
    }
    return order.map(index => subCards[index]);
}

function blockText(block: BlockMarkup): string | undefined {
    if (block.some(item => item.type !== BlockMarkupItemType.PlainText))
        return undefined;
    return block.map(item => (item as PlainTextMarkup).text).join('\n');
}

/**
 * What the car voices for a card. A card with subcards is voiced only through
 * them, as in Review; a card without subcards falls back to its question/answer.
 * Undefined when nothing on the card can be voiced (images, code, empty sides).
 */
export function buildCarPlan(markup: string): CarCardPlan | undefined {
    const tree = markupFormatter.parse(markup);

    if (tree.subCards.length > 0) {
        const items = selectCarSubcards(tree.subCards)
            .map(subcardItem)
            .filter((item): item is CarItem => item !== undefined);
        return items.length > 0 ? { hasSubcards: true, items } : undefined;
    }

    const question = blockText(tree.question);
    const answer = blockText(tree.answer);
    if (question === undefined || answer === undefined)
        return undefined;

    const item = voiceItem(textSegments(question), answer, {});
    return item !== undefined ? { hasSubcards: false, items: [item] } : undefined;
}
